/**
 * Central gate for live writes made while a platform admin is viewing as
 * another account (MOT-169).
 *
 * Every request the app makes goes through one of two clients: `makeApiCall`
 * (fetch) and the shared axios instance. Both call `confirmLiveWrite` before a
 * mutating request leaves the browser. The dialog itself is rendered by
 * `ImpersonationWriteGuardProvider`, which registers itself here, so no page
 * needs to know about the guard.
 *
 * Reads the same `admin-impersonation` localStorage entry that
 * `getImpersonationHeaders` turns into `X-Impersonation-*` headers, so the
 * gate and the backend agree on whether a request is impersonated.
 */

export type LiveWriteRequest = { method: string; url: string };
export type LiveWriteConfirmHandler = (request: LiveWriteRequest) => Promise<boolean>;

/** Thrown to the caller when the admin cancels the confirmation. */
export class LiveWriteDeclinedError extends Error {
  constructor() {
    super('Change cancelled. It was not confirmed while viewing as another account.');
    this.name = 'LiveWriteDeclinedError';
  }
}

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Requests that mutate nothing a brand or store owns: session bootstrap, the
 * admin's own active-organization preference, and generators that only return
 * text. Everything else that is not a GET is treated as a live write.
 */
const EXEMPT_PATHS: RegExp[] = [
  /\/session(\/|\?|$)/,
  /\/ai\//,
  /\/code-snippets\/generate/,
  // POSTs that only compute and return a result. Each is a full-path match:
  // never a suffix pattern, which would also exempt real writes.
  /^(\/api)?\/store\/sku-overrides\/preview(\?|$)/,
  /^(\/api)?\/integrations\/[^/]+\/description-preview(\?|$)/,
  /^(\/api)?\/store\/bundle-rebates\/evaluate(\?|$)/,
  /^(\/api)?\/store\/taxonomy-classifier\/classify(\?|$)/,
  // A batch price lookup: a POST because the model list is a body, but it reads
  // only (the API's own `READ_ONLY_POST_PATTERNS` says the same). The product
  // browser fires it for every page of rows, so without this the dialog opened
  // on its own as soon as the grid loaded while viewing as a store.
  /^(\/api)?\/map-pricing\/by-models(\?|$)/,
];

/**
 * The path of a request URL, for the full-path patterns above. `makeApiCall`
 * passes a path, while axios callers pass the API base URL joined to it, which
 * in production is an absolute URL; both must match the same way.
 */
const requestPath = (url: string): string => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) {
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  }
  return url;
};

let handler: LiveWriteConfirmHandler | null = null;
// The impersonation identity the admin already approved live changes for. One
// confirmation covers the whole impersonation; switching account asks again.
let approvedIdentity: string | null = null;
let confirmationQueue: Promise<void> = Promise.resolve();

export const setLiveWriteConfirmHandler = (next: LiveWriteConfirmHandler): (() => void) => {
  handler = next;
  return () => {
    if (handler === next) handler = null;
  };
};

/** Forget the approval, for example when impersonation ends. */
export const resetLiveWriteApproval = (): void => {
  approvedIdentity = null;
};

export const isImpersonatingNow = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem('admin-impersonation');
    if (!raw) return false;
    return Boolean(JSON.parse(raw)?.isImpersonating);
  } catch {
    return false;
  }
};

export const requiresLiveWriteConfirmation = (method: string | undefined, url: string | undefined): boolean => {
  const upper = (method || 'GET').toUpperCase();
  if (!WRITE_METHODS.has(upper)) return false;
  if (!isImpersonatingNow()) return false;
  const path = requestPath(url || '');
  return !EXEMPT_PATHS.some((pattern) => pattern.test(path));
};

/** Confirms once per impersonation, serialized so dialogs cannot overlap. */
export const confirmLiveWrite = async (method: string | undefined, url: string | undefined): Promise<void> => {
  if (!requiresLiveWriteConfirmation(method, url)) return;
  const identity = window.localStorage.getItem('admin-impersonation');
  const confirm = async () => {
    if (identity !== null && approvedIdentity === identity) return;
    if (!handler || window.localStorage.getItem('admin-impersonation') !== identity) {
      throw new LiveWriteDeclinedError();
    }
    const approved = await handler({ method: (method || 'POST').toUpperCase(), url: url || '' });
    if (!approved || window.localStorage.getItem('admin-impersonation') !== identity) {
      throw new LiveWriteDeclinedError();
    }
    approvedIdentity = identity;
  };
  const pending = confirmationQueue.then(confirm);
  confirmationQueue = pending.catch(() => undefined);
  await pending;
};
